import { Plus } from "lucide-react";
import type React from "react";
import { useState, useMemo } from "react";
import { useSearchParams, useRouter } from "next/navigation";

import { Button } from "@/app/_module/components/ui/button";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/app/_module/components/ui/tabs";

import { LeaveVoicemailModal } from "./LeaveVoicemailModal";
import { VoicemailFilters } from "./VoicemailFilters";
import { VoicemailList } from "./VoicemailList";

interface VoicemailRecord {
  id: string;
  leadId: string;
  phoneNumber: string;
  contactName?: string;
  direction: "inbound" | "outbound";
  duration: number;
  audioUrl: string;
  transcript?: string;
  isListened: boolean;
  timestamp: Date;
  callId?: string;
  notes?: string;
  priority: "low" | "normal" | "high";
}

interface Lead {
  id: string;
  phone?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

interface VoicemailTabProps {
  voicemails: VoicemailRecord[];
  onMarkAsListened: (id: string) => void;
  onDeleteVoicemail: (id: string) => void | Promise<void>;
  onAddVoicemail: (voicemail: VoicemailRecord) => void;
  leads?: Lead[]; // For quick access to leave voicemail
}

type VoicemailDirectionFilter = "all" | "inbound" | "outbound";
type VoicemailStatusFilter = "all" | "listened" | "unlistened";

export const VoicemailTab: React.FC<VoicemailTabProps> = function({
  voicemails,
  onMarkAsListened,
  onDeleteVoicemail,
  onAddVoicemail,
  leads = []
}) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState("");

  // Get voicemail sub-tab from URL
  const voicemailSubTab = searchParams.get("voicemailSubTab") || "received";

  // Handle voicemail sub-tab change
  const handleVoicemailSubTabChange = (value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("voicemailSubTab", value);
    router.replace(`?${params.toString()}`, { scroll: false });
  };
  const [directionFilter, setDirectionFilter] = useState<VoicemailDirectionFilter>("all");
  const [statusFilter, setStatusFilter] = useState<VoicemailStatusFilter>("all");
  const [showLeaveVoicemailModal, setShowLeaveVoicemailModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);

  // Filter voicemails based on search and filters
  const filteredVoicemails = useMemo(() => {
    return voicemails.filter((voicemail) => {
      const matchesSearch = 
        searchQuery === "" ||
        voicemail.phoneNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        voicemail.contactName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        voicemail.transcript?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        voicemail.notes?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesDirection = 
        directionFilter === "all" || voicemail.direction === directionFilter;
      
      const matchesStatus = 
        statusFilter === "all" || 
        (statusFilter === "listened" && voicemail.isListened) ||
        (statusFilter === "unlistened" && !voicemail.isListened);

      return matchesSearch && matchesDirection && matchesStatus;
    });
  }, [voicemails, searchQuery, directionFilter, statusFilter]);

  // Separate received and sent voicemails
  const receivedVoicemails = filteredVoicemails.filter(v => v.direction === "inbound");
  const sentVoicemails = filteredVoicemails.filter(v => v.direction === "outbound");

  // Count unread received voicemails
  const unreadCount = receivedVoicemails.filter(v => !v.isListened).length;

  const handleReply = (voicemail: VoicemailRecord) => {
    // Find the lead for this voicemail or create a temporary one
    const lead = leads.find(l => l.id === voicemail.leadId) || {
      id: voicemail.leadId,
      phone: voicemail.phoneNumber,
      firstName: voicemail.contactName?.split(' ')[0],
      lastName: voicemail.contactName?.split(' ').slice(1).join(' '),
    };
    
    setSelectedLead(lead);
    setShowLeaveVoicemailModal(true);
  };

  const handleLeaveVoicemail = () => {
    if (leads.length > 0) {
      setSelectedLead(leads[0]); // Default to first lead or implement lead selector
      setShowLeaveVoicemailModal(true);
    } else {
      // Create a default lead for demo purposes
      setSelectedLead({
        id: "demo-lead",
        phone: "",
        firstName: "New",
        lastName: "Contact"
      });
      setShowLeaveVoicemailModal(true);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Voicemail Center</h2>
          <p className="text-sm text-muted-foreground">
            Manage your voicemail messages
          </p>
        </div>
      </div>

      <div className="bg-card rounded-lg shadow-sm border p-6">
        <VoicemailFilters
          directionFilter={directionFilter}
          onDirectionChange={setDirectionFilter}
          onSearchChange={setSearchQuery}
          onStatusChange={setStatusFilter}
          searchQuery={searchQuery}
          statusFilter={statusFilter}
        />

        <div className="mt-6">
          <Tabs className="w-full" value={voicemailSubTab} onValueChange={handleVoicemailSubTabChange}>
            <TabsList className="inline-flex w-full max-w-sm">
              <TabsTrigger className="flex-1 relative" value="received">
                Received
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="sent" className="flex-1">
                Sent
              </TabsTrigger>
            </TabsList>

            <TabsContent className="space-y-4" value="received">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-foreground">
                  Received Voicemails ({receivedVoicemails.length})
                </h3>
                {unreadCount > 0 && (
                  <span className="text-sm text-blue-600 font-medium">
                    {unreadCount} unread
                  </span>
                )}
              </div>
              <VoicemailList
                onDelete={onDeleteVoicemail}
                onMarkAsListened={onMarkAsListened}
                onReply={handleReply}
                type="received"
                voicemails={receivedVoicemails}
              />
            </TabsContent>

            <TabsContent className="space-y-4" value="sent">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-foreground">
                  Sent Voicemails ({sentVoicemails.length})
                </h3>
              </div>
              <VoicemailList
                onDelete={onDeleteVoicemail}
                onMarkAsListened={onMarkAsListened}
                onReply={handleReply}
                type="sent"
                voicemails={sentVoicemails}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Leave Voicemail Modal */}
      {!!selectedLead && (
        <LeaveVoicemailModal
          lead={selectedLead}
          onOpenChange={setShowLeaveVoicemailModal}
          onSend={onAddVoicemail}
          open={showLeaveVoicemailModal}
        />
      )}
    </div>
  );
}
